import {
  GetMyFollowedEntitiesResponse,
  GetMyFollowedEntityInfosResponse,
} from '@upright/common/iam/organizations/organization-types';
import { useMutation, useQuery, useQueryClient, queryOptions } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { followEntity, getMyFollowedEntities, getMyFollowedEntityInfos, unfollowEntity } from '.';
import { ModelEntityReference } from '../../../../common/model/input/types';
import { useGetLoggedInUser } from '../../../common/generic/hooks';
import { getVersionAndRelease } from '../../../untidy/redux/selectors';
import { UserPrivate } from '@upright/common/iam/users/types';

const getMyFollowedEntitiesOptions = (user: UserPrivate | undefined) =>
  queryOptions({
    queryKey: ['myFollowedEntities', { id: user?.id }],
    queryFn: getMyFollowedEntities,
    gcTime: Infinity,
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
    enabled: !!user,
  });

export function useMyFollowedEntitiesQuery() {
  const user = useGetLoggedInUser();
  return useQuery(getMyFollowedEntitiesOptions(user));
}

/**
 * The favorited entities with their names and scores, rather than the bare references
 * `useMyFollowedEntitiesQuery` returns.
 *
 * Two surfaces read this — the `/favorites` table and the investor launcher's Favorites section
 * (PLT-6702) — and they must agree on what is in the list, so the query lives here rather than in
 * either view. The key is the one `useMutateMyFollowedEntities` already invalidates and patches
 * optimistically, so starring an entity updates both surfaces without either knowing about the
 * other; a second `useQuery` with the same key would have worked and is exactly how the two would
 * drift the first time one of them changed its options.
 *
 * The release comes from the store because an entity's scores are release-scoped, and the query
 * waits for it: the endpoint takes the release in its path.
 */
export function useMyFollowedEntityInfosQuery() {
  const user = useGetLoggedInUser();
  const { release } = useSelector(getVersionAndRelease);

  return useQuery({
    queryKey: ['myFavoritedEntitiesInfos', user?.id],
    queryFn: async () => await getMyFollowedEntityInfos(release!),
    refetchOnWindowFocus: false,
    enabled: !!release,
  });
}

export function usePreFetchMyFollowedEntities() {
  const queryClient = useQueryClient();
  const user = useGetLoggedInUser();

  const prefetchData = async () => {
    if (!user) return;
    await queryClient.prefetchQuery(getMyFollowedEntitiesOptions(user));
  };

  void prefetchData();
}

/**
 * Whether one entity is favorited, and the toggle that flips it.
 *
 * Two controls need this and must not answer differently: the legacy title bar's bare star
 * (`StarredEntity`) and the unified shell's labelled ghost button (`FavoriteToggleButton`). The
 * query and the mutation were previously read inside the star itself, which is why the second
 * control could not reuse them without copying the `isStarred` derivation too — and a copied
 * derivation is how a control ends up saying "Add" about something already saved.
 *
 * `loading` covers only the first read. Afterwards the mutation patches the cache optimistically,
 * so a toggle flips the label immediately rather than waiting for the round trip.
 */
export function useFavoriteToggle(entityRef: ModelEntityReference) {
  const query = useMyFollowedEntitiesQuery();
  const mutation = useMutateMyFollowedEntities();

  const isStarred = query.data?.some(ref => ref.id === entityRef.id && ref.type === entityRef.type) ?? false;

  return {
    isStarred,
    loading: query.isPending,
    error: query.status === 'error' ? query.error : null,
    toggle: () => {
      mutation.mutate({ entityRef, isStarred });
    },
  };
}

interface MutateMyFollowedEntitiesInput {
  entityRef: ModelEntityReference;
  isStarred: boolean;
}

export function useMutateMyFollowedEntities() {
  const queryClient = useQueryClient();
  const user = useGetLoggedInUser();

  return useMutation({
    mutationFn: async (input: MutateMyFollowedEntitiesInput) => {
      switch (input.isStarred) {
        case true:
          return await unfollowEntity(input.entityRef);
        case false:
          return await followEntity(input.entityRef);
      }
    },

    onMutate: input => {
      const previousFollowedEntities = queryClient.getQueryData<GetMyFollowedEntitiesResponse>([
        'myFollowedEntities',
        { id: user?.id },
      ]);
      if (previousFollowedEntities) {
        switch (input.isStarred) {
          case true:
            queryClient.setQueryData<GetMyFollowedEntitiesResponse>(
              ['myFollowedEntities', { id: user?.id }],
              previousFollowedEntities.filter(
                ref => ref.id !== input.entityRef.id || ref.type !== input.entityRef.type,
              ),
            );
            break;
          case false:
            queryClient.setQueryData<GetMyFollowedEntitiesResponse>(
              ['myFollowedEntities', { id: user?.id }],
              [...previousFollowedEntities, input.entityRef],
            );
            break;
        }
      }

      const previousFollowedEntityInfos = queryClient.getQueryData<GetMyFollowedEntityInfosResponse>([
        'myFavoritedEntitiesInfos',
        user?.id,
      ]);
      if (previousFollowedEntityInfos) {
        switch (input.isStarred) {
          case true:
            queryClient.setQueryData<GetMyFollowedEntityInfosResponse>(
              ['myFavoritedEntitiesInfos', user?.id],
              previousFollowedEntityInfos.filter(
                ref => ref.id !== input.entityRef.id || ref.type !== input.entityRef.type,
              ),
            );
            break;
          case false:
            break;
        }
      }

      return { previousFollowedEntities, previousFollowedEntityInfos };
    },

    onError: (_err, _variables, context) => {
      if (context?.previousFollowedEntities) {
        queryClient.setQueryData(['myFollowedEntities', { id: user?.id }], context.previousFollowedEntities);
      }
      if (context?.previousFollowedEntityInfos) {
        queryClient.setQueryData(['myFavoritedEntitiesInfos', user?.id], context.previousFollowedEntityInfos);
      }
    },

    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: ['myFollowedEntities', { id: user?.id }],
      });
      void queryClient.invalidateQueries({
        queryKey: ['myFavoritedEntitiesInfos', user?.id],
      });
    },
  });
}
